'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function GasMileageCalculator() {
  const t = useTranslations("tools.gasMileageCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentUnit, setCurrentUnit] = useState<'us' | 'metric'>('us');
  const [showResults, setShowResults] = useState(false);

  const [usInputs, setUsInputs] = useState({
    currentOdometer: 12360,
    previousOdometer: 12000,
    gasAdded: 15,
    gasPrice: 3.50,
  });

  const [metricInputs, setMetricInputs] = useState({
    currentOdometer: 18900,
    previousOdometer: 18300,
    gasAdded: 50,
    gasPrice: 1.20,
  });

  const [results, setResults] = useState({
    mileage: 0,
    mileageUnit: 'MPG',
    distance: 0,
    distanceUnit: 'miles',
    totalCost: 0,
    costPerDistance: 0,
  });

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const inputs = currentUnit === 'us' ? usInputs : metricInputs;
    const { currentOdometer, previousOdometer, gasAdded, gasPrice } = inputs;

    if (currentOdometer <= previousOdometer) {
      alert(t('errorCurrentLessThanPrevious'));
      return;
    }

    if (gasAdded <= 0) {
      alert(t('errorGasZero'));
      return;
    }

    const distance = currentOdometer - previousOdometer;
    const mileage = distance / gasAdded;
    const totalCost = gasAdded * gasPrice;
    const costPerDistance = totalCost / distance;

    setResults({
      mileage,
      mileageUnit: currentUnit === 'us' ? 'MPG' : 'km/L',
      distance,
      distanceUnit: currentUnit === 'us' ? 'miles' : 'km',
      totalCost,
      costPerDistance,
    });

    setShowResults(true);

    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleClear = () => {
    if (currentUnit === 'us') {
      setUsInputs({
        currentOdometer: 0,
        previousOdometer: 0,
        gasAdded: 0,
        gasPrice: 0,
      });
    } else {
      setMetricInputs({
        currentOdometer: 0,
        previousOdometer: 0,
        gasAdded: 0,
        gasPrice: 0,
      });
    }
    setShowResults(false);
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const subtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '100%',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
    },
    { maxWidth: '100%' },
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const unitTabsStyle: React.CSSProperties = {
    display: 'flex',
    background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
  };

  const unitTabStyle = (isActive: boolean) => ({
    flex: 1,
    padding: screenSize === 'mobile' ? '0.75rem 0.5rem' : '1rem',
    textAlign: 'center' as const,
    background: isActive ? 'white' : 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: screenSize === 'mobile' ? '0.9rem' : '1rem',
    fontWeight: 600,
    color: isActive ? '#2563eb' : '#64748b',
    borderBottom: isActive ? '3px solid #2563eb' : 'none',
    transition: 'all 0.3s ease',
  });

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputWrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: '#f8fafc',
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' }
  );

  const unitLabelStyle: React.CSSProperties = {
    color: '#64748b',
    fontWeight: 500,
    minWidth: screenSize === 'mobile' ? '60px' : '80px',
    textAlign: 'left' as const,
    fontSize: screenSize === 'mobile' ? '0.85rem' : '0.9rem',
  };

  const optionalTagStyle: React.CSSProperties = {
    display: 'inline-block',
    background: '#f59e0b',
    color: 'white',
    padding: '0.2rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.75rem',
    marginLeft: '0.5rem',
    fontWeight: 600,
  };

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: screenSize === 'mobile' ? '0.75rem' : '1rem',
    marginTop: '2rem',
    flexDirection: screenSize === 'mobile' ? 'column' : 'row',
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.05rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.05rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      border: '2px solid #86efac',
    },
    { margin: '1.5rem', padding: '1.5rem' },
    { margin: '1.75rem', padding: '1.75rem' },
    { margin: '2rem', padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#166534', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.35rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
  };

  const resultItemStyle: React.CSSProperties = {
    background: 'white',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  };

  const resultLabelStyle: React.CSSProperties = {
    display: 'block',
    color: '#64748b',
    fontSize: '0.9rem',
    marginBottom: '0.5rem',
    fontWeight: 500,
  };

  const resultValueStyle = (isPrimary?: boolean, isCost?: boolean) => getResponsiveStyle(
    {
      display: 'block',
      color: isPrimary ? '#2563eb' : isCost ? '#f59e0b' : colors.textPrimary,
      fontWeight: 700,
    },
    { fontSize: isPrimary ? '1.85rem' : '1.5rem' },
    { fontSize: isPrimary ? '2rem' : '1.65rem' },
    { fontSize: isPrimary ? '2.25rem' : '1.75rem' }
  );

  const infoBoxStyle = getResponsiveStyle(
    {
      background: '#fef3c7',
      borderLeft: '4px solid #f59e0b',
      borderRadius: '0.5rem',
    },
    { margin: '1.5rem', padding: '1.25rem' },
    { margin: '1.75rem', padding: '1.4rem' },
    { margin: '2rem', padding: '1.5rem' }
  );

  const infoBoxPStyle: React.CSSProperties = {
    margin: 0,
    color: '#92400e',
    lineHeight: 1.6,
    fontSize: screenSize === 'mobile' ? '0.95rem' : '1rem',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const strongStyle: React.CSSProperties = {
    color: colors.primary,
    fontWeight: 600,
  };

  const faqItemStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: 0 },
    { fontSize: '0.95rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const refListStyle: React.CSSProperties = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  };

  const refItemStyle: React.CSSProperties = {
    marginBottom: '0.75rem',
    paddingLeft: '1.5rem',
    position: 'relative' as const,
  };

  const refLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const,
    fontSize: screenSize === 'mobile' ? '0.9rem' : '1rem',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={subtitleStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>⛽ {t('toolTitle')}</h2>
          </div>

          <div style={unitTabsStyle}>
            <button
              style={unitTabStyle(currentUnit === 'us')}
              onClick={() => { setCurrentUnit('us'); setShowResults(false); }}
              onMouseEnter={(e) => {
                if (currentUnit !== 'us') {
                  e.currentTarget.style.background = '#e0f2fe';
                  e.currentTarget.style.color = '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (currentUnit !== 'us') {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#64748b';
                }
              }}
            >
              {t('usUnits')}
            </button>
            <button
              style={unitTabStyle(currentUnit === 'metric')}
              onClick={() => { setCurrentUnit('metric'); setShowResults(false); }}
              onMouseEnter={(e) => {
                if (currentUnit !== 'metric') {
                  e.currentTarget.style.background = '#e0f2fe';
                  e.currentTarget.style.color = '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (currentUnit !== 'metric') {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#64748b';
                }
              }}
            >
              {t('metricUnits')}
            </button>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            {currentUnit === 'us' ? (
              <>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="us-current">{t('currentOdometer')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="us-current"
                      step="0.1"
                      min="0"
                      value={usInputs.currentOdometer || ''}
                      onChange={(e) => setUsInputs({...usInputs, currentOdometer: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <span style={unitLabelStyle}>{t('miles')}</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="us-previous">{t('previousOdometer')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="us-previous"
                      step="0.1"
                      min="0"
                      value={usInputs.previousOdometer || ''}
                      onChange={(e) => setUsInputs({...usInputs, previousOdometer: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <span style={unitLabelStyle}>{t('miles')}</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="us-gas">{t('gasAdded')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="us-gas"
                      step="0.01"
                      min="0.01"
                      value={usInputs.gasAdded || ''}
                      onChange={(e) => setUsInputs({...usInputs, gasAdded: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <span style={unitLabelStyle}>{t('gallons')}</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="us-price">
                    {t('gasPrice')}
                    <span style={optionalTagStyle}>{t('optional')}</span>
                  </label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="us-price"
                      step="0.01"
                      min="0"
                      value={usInputs.gasPrice || ''}
                      onChange={(e) => setUsInputs({...usInputs, gasPrice: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <span style={unitLabelStyle}>{t('perGallon')}</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="metric-current">{t('currentOdometer')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="metric-current"
                      step="0.1"
                      min="0"
                      value={metricInputs.currentOdometer || ''}
                      onChange={(e) => setMetricInputs({...metricInputs, currentOdometer: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <span style={unitLabelStyle}>{t('kilometers')}</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="metric-previous">{t('previousOdometer')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="metric-previous"
                      step="0.1"
                      min="0"
                      value={metricInputs.previousOdometer || ''}
                      onChange={(e) => setMetricInputs({...metricInputs, previousOdometer: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <span style={unitLabelStyle}>{t('kilometers')}</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="metric-gas">{t('gasAdded')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="metric-gas"
                      step="0.01"
                      min="0.01"
                      value={metricInputs.gasAdded || ''}
                      onChange={(e) => setMetricInputs({...metricInputs, gasAdded: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <span style={unitLabelStyle}>{t('liters')}</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="metric-price">
                    {t('gasPrice')}
                    <span style={optionalTagStyle}>{t('optional')}</span>
                  </label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="metric-price"
                      step="0.01"
                      min="0"
                      value={metricInputs.gasPrice || ''}
                      onChange={(e) => setMetricInputs({...metricInputs, gasPrice: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#2563eb';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <span style={unitLabelStyle}>{t('perLiter')}</span>
                  </div>
                </div>
              </>
            )}

            <div style={buttonGroupStyle}>
              <button
                type="submit"
                style={calculateBtnStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
                }}
              >
                {t('calculateButton')}
              </button>
              <button
                type="button"
                style={clearBtnStyle}
                onClick={handleClear}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                }}
              >
                {t('clearButton')}
              </button>
            </div>
          </form>

          {showResults && (
            <div style={resultsStyle} id="results-section">
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>
              <div style={resultGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('gasMileageLabel')}</span>
                  <span style={resultValueStyle(true)}>
                    {results.mileage.toFixed(2)} {results.mileageUnit}
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('distanceTraveledLabel')}</span>
                  <span style={resultValueStyle()}>
                    {results.distance.toFixed(1)} {results.distanceUnit}
                  </span>
                </div>
                {(currentUnit === 'us' ? usInputs.gasPrice : metricInputs.gasPrice) > 0 && (
                  <>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('totalFuelCostLabel')}</span>
                      <span style={resultValueStyle(false, true)}>
                        ${results.totalCost.toFixed(2)}
                      </span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('costPerDistanceLabel')}</span>
                      <span style={resultValueStyle(false, true)}>
                        ${results.costPerDistance.toFixed(3)} {t('per')} {currentUnit === 'us' ? t('mile') : t('km')}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div style={infoBoxStyle}>
            <p style={infoBoxPStyle}>
              <strong>💡 {t('tipLabel')}</strong> {t('tipText')}
            </p>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Text')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Text')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Text')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Text')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Text')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insight1')}</p>
        <p style={paragraphStyle}>{t('insight2')}</p>
        <p style={paragraphStyle}>{t('insight3')}</p>
        <p style={paragraphStyle}>{t('insight4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t('useCasesTitle')}</h3>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t('useCase1Title')}</strong> {t('useCase1Text')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t('useCase2Title')}</strong> {t('useCase2Text')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t('useCase3Title')}</strong> {t('useCase3Text')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t('useCase4Title')}</strong> {t('useCase4Text')}</li>
          <li style={{marginBottom: '0.5rem'}}><strong style={strongStyle}>{t('useCase5Title')}</strong> {t('useCase5Text')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t('bestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.5rem'}}>{t('practice1')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('practice2')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('practice3')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('practice4')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('practice5')}</li>
          <li style={{marginBottom: '0.5rem'}}>{t('practice6')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={refListStyle}>
          <li style={refItemStyle}>
            <span style={{position: 'absolute', left: 0, color: colors.primary}}>1.</span>
            <a
              href="https://nextbillion.ai/blog/ultimate-guide-to-mileage-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              {t('ref1')}
            </a>
          </li>
          <li style={refItemStyle}>
            <span style={{position: 'absolute', left: 0, color: colors.primary}}>2.</span>
            <a
              href="https://www.zintego.com/blog/how-to-calculate-gas-mileage-a-step-by-step-guide/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              {t('ref2')}
            </a>
          </li>
          <li style={refItemStyle}>
            <span style={{position: 'absolute', left: 0, color: colors.primary}}>3.</span>
            <a
              href="https://www.mburse.com/blog/how-to-calculate-gas-mileage-for-reimbursement-rates"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              {t('ref3')}
            </a>
          </li>
          <li style={refItemStyle}>
            <span style={{position: 'absolute', left: 0, color: colors.primary}}>4.</span>
            <a
              href="https://www.calculator.net/gas-mileage-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              {t('ref4')}
            </a>
          </li>
          <li style={refItemStyle}>
            <span style={{position: 'absolute', left: 0, color: colors.primary}}>5.</span>
            <a
              href="https://www.fueleconomy.gov"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              {t('ref5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}